import React, { useState, useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLocale } from '../../contexts/LocaleContext';
import { PageHeaderProvider } from '../../contexts/PageHeaderContext';
import { spacing } from '../../config/spacing';
import { themeClasses } from '../../config/themeClasses';
import Sidebar from './Sidebar';
import Header from './Header';
import CommandPalette, { useCommandPalette } from './CommandPalette';

const DashboardLayout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mainMarginLeft, setMainMarginLeft] = useState(0);
  const { user, logout } = useAuth();
  const { t } = useLocale();
  const location = useLocation();
  const navigate = useNavigate();
  const { open: commandOpen, setOpen: setCommandOpen } = useCommandPalette();

  useEffect(() => {
    const updateMargin = () => {
      setMainMarginLeft(
        window.innerWidth >= 1024
          ? sidebarCollapsed
            ? spacing.layout.sidebar.collapsed
            : spacing.layout.sidebar.expanded
          : 0
      );
    };
    updateMargin();
    window.addEventListener('resize', updateMargin);
    return () => window.removeEventListener('resize', updateMargin);
  }, [sidebarCollapsed]);

  useEffect(() => {
    const savedCollapsedState = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsedState !== null) {
      setSidebarCollapsed(JSON.parse(savedCollapsedState));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(sidebarCollapsed));
  }, [sidebarCollapsed]);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (
    location.pathname === '/login' ||
    location.pathname === '/signup' ||
    location.pathname === '/forgot-password'
  ) {
    return <Outlet />;
  }

  return (
    <div className="min-h-screen bg-background-primary">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        user={user}
        onLogout={handleLogout}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />

      <PageHeaderProvider>
        <div
          className="flex min-h-screen flex-col transition-all duration-300 ease-in-out"
          style={{ marginLeft: mainMarginLeft }}
        >
          <Header
            onMenuClick={() => setSidebarOpen(true)}
            user={user}
            onLogout={handleLogout}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed(!sidebarCollapsed)}
            onOpenCommandPalette={() => setCommandOpen(true)}
          />
          <CommandPalette open={commandOpen} onOpenChange={setCommandOpen} />

          <a
            href="#main-content"
            className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:m-4 focus:rounded-sm focus:bg-background-card focus:px-4 focus:py-2 focus:text-text-primary focus:shadow-elevation-3 focus:ring-2 focus:ring-primary-500"
          >
            {t('skipToMainContent')}
          </a>
          <main id="main-content" className="flex-1 bg-background-primary">
            <div className={`page-shell ${themeClasses.layout.mainContentMax}`}>
              <div className="animate-fade-in">
                <div className="grid grid-cols-12 gap-6">
                  <div className="col-span-12">
                    <Outlet />
                  </div>
                </div>
              </div>
            </div>
            <footer
              className={`border-t border-border-light py-4 ${themeClasses.layout.mainContentMax} px-4 sm:px-6 lg:px-8`}
            >
              <p className="text-center text-xs text-text-tertiary">
                {t('footerCopyright')}
              </p>
            </footer>
          </main>
        </div>
      </PageHeaderProvider>
    </div>
  );
};

export default DashboardLayout;
